import {
  Button,
  Card,
  Column,
  Dropdown,
  DropdownItem,
  Pagination,
  Separator,
  Table,
} from 'platform/components';
import {Heading, HStack, Text, VStack} from 'platform/foundation';

import {Fragment, ReactNode, useState} from 'react';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  SALES_LEAGUE_ROWS_PER_PAGE,
  SALES_LEAGUE_ROWS_PER_PAGE_OPTIONS,
} from '../constants/SALES_LEAGUE_ROWS_PER_PAGE';
import {getSalesLeaguePageCount, paginateSalesLeagueRows} from '../utils/paginateSalesLeagueRows';

interface SalesLeagueBoardTableProps<TRow> extends RequiredTestIdProps {
  title: string;
  subtitle: string;
  columns: Column[];
  rows: TRow[];
  getRowKey: (row: TRow) => string;
  /** `index` is the row's position in `rows`, not in the current page. */
  renderRow: (row: TRow, index: number) => ReactNode;
  /** Bottom-of-card metric definitions, paired with the period the board covers. */
  footnote: string;
  periodLabel: string;
}

export function SalesLeagueBoardTable<TRow>(props: SalesLeagueBoardTableProps<TRow>) {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(SALES_LEAGUE_ROWS_PER_PAGE);

  const handleRowsPerPageChange = (value: number) => {
    setRowsPerPage(value);
    setPage(1);
  };

  const pageCount = getSalesLeaguePageCount(props.rows.length, rowsPerPage);
  const safePage = Math.min(page, pageCount);
  const pageRows = paginateSalesLeagueRows(props.rows, safePage, rowsPerPage);

  const rangeFrom = props.rows.length === 0 ? 0 : (safePage - 1) * rowsPerPage + 1;
  const rangeTo = Math.min(safePage * rowsPerPage, props.rows.length);

  // Rows are paginated, so the page-local index is offset back to the full-list index —
  // a row that needs its neighbour must not pick up the wrong one on page two.
  const pageOffset = (safePage - 1) * rowsPerPage;

  return (
    <Card>
      <VStack spacing={4}>
        <VStack spacing={1}>
          <Heading size={4}>{props.title}</Heading>
          <Text size="small" color="secondary">
            {props.subtitle}
          </Text>
        </VStack>

        <Table columns={props.columns} data-testid={suffixTestId('table', props)}>
          {pageRows.map((row, pageIndex) => (
            <Fragment key={props.getRowKey(row)}>
              {props.renderRow(row, pageOffset + pageIndex)}
            </Fragment>
          ))}
        </Table>

        <HStack justify="space-between" align="flex-end" spacing={4}>
          <Text size="xSmall" color="tertiary">
            {props.footnote}
          </Text>
          <Text size="xSmall" color="tertiary" noWrap>
            {props.periodLabel}
          </Text>
        </HStack>

        <Separator spacing={0} />

        <HStack justify="space-between" align="center" spacing={4}>
          <HStack spacing={3} align="center">
            <Text size="small" color="secondary">
              {i18n.t('page.datagrid.labels.rowsPerPage')}
            </Text>
            <Dropdown
              dropdownControl={
                <Button
                  variant="secondary"
                  title={rowsPerPage.toString()}
                  rightIcon="navigation/expand_more"
                  data-testid={suffixTestId('rowsPerPage-trigger', props)}
                />
              }
              data-testid={suffixTestId('rowsPerPage', props)}
            >
              {SALES_LEAGUE_ROWS_PER_PAGE_OPTIONS.map((option) => (
                <DropdownItem
                  key={option}
                  label={option.toString()}
                  onClick={() => handleRowsPerPageChange(option)}
                  data-testid={suffixTestId(`rowsPerPage-item-${option}`, props)}
                />
              ))}
            </Dropdown>
            <Text size="small" color="secondary">
              {i18n.t('page.salesLeague.table.range', {
                from: rangeFrom,
                to: rangeTo,
                total: props.rows.length,
              })}
            </Text>
          </HStack>
          <HStack spacing={4} align="center">
            <Pagination
              pagesQuantity={pageCount}
              page={safePage}
              onPageChange={setPage}
              showFirstButton
              showLastButton
              showPrevButton
              showNextButton
              data-testid={suffixTestId('pagination', props)}
            />
          </HStack>
        </HStack>
      </VStack>
    </Card>
  );
}
